import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
    useParams,
    useSearchParams,
    useNavigate,
    useLocation,
} from "react-router-dom";
// material
import {
    Container,
    Stack,
    Typography,
    Pagination,
    LinearProgress,
    Menu,
    MenuItem,
    Card,
    CardContent,
} from "@mui/material";
import { Box } from "@mui/system";
// components
import Page from "../components/Page";
import { ProductList } from "../components/dashboard/products";

import ContentSortMenu from "../components/ContentSortMenu";
import Loader from "src/components/loader/Loader";
import ContentDialog from "src/components/dashboard/admin/Content/ContentDialog";

import { useDispatch, useSelector } from "react-redux";
import { post } from "src/services/content.service";
import { useTranslation } from "react-i18next";
import SelectionBar from "src/components/SelectionBar";
import DownloadButton from "src/components/dashboard/admin/utlis/DownloadButton";
import Page404 from "./Page404";
import saveAs from "file-saver";
import { showAlert } from "src/redux/slices/AlertSlice";
import ConfirmDialog from "src/components/dashboard/admin/Popup/ConfirmDialog";
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    useSensor,
    useSensors,
    pointerWithin,
} from "@dnd-kit/core";
import {
    SortableContext,
    rectSortingStrategy,
    arrayMove,
} from "@dnd-kit/sortable";

const DEFAULT_CONTENT_PER_PAGE = [
    { value: "12", label: "12" },
    { value: "24", label: "24" },
    { value: "36", label: "36" },
];
// ----------------------------------------------------------------------

/** Render a content grid with selection and administrator bulk actions. */
export default function ContentList({ endpoint, altSlug, label }) {
    const params = useParams();
    const slug = params.slug ? params.slug : altSlug;

    const [content, setContent] = useState([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(-1);
    const [pageCount, setPageCount] = useState(
        DEFAULT_CONTENT_PER_PAGE[1].value
    );
    const [loading, setLoading] = useState(false);
    const [title, setTitle] = useState(label);
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const isAdmin = user && user.is_staff === true;
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const tab = searchParams.get("tab");
    const { t } = useTranslation();
    const [selected, setSelected] = useState([]);
    const [progress, setProgress] = useState({});
    const [parent, setParent] = useState(null);
    const [activeId, setActiveId] = useState(null);
    const [activeDrag, setActiveDrag] = useState(false);
    const [suppressNavigationClick, setSuppressNavigationClick] = useState(false);
    const initialOrderRef = useRef(new Map());
    const dndMovedRef = useRef(false);
    const [contextMenu, setContextMenu] = useState(null);
    const [contextItem, setContextItem] = useState(null);
    const contextItemRef = useRef(null);
    const [showModal, setShowModal] = useState(false);
    const [dialogContentType, setDialogContentType] = useState(null);
    const [dialogSlug, setDialogSlug] = useState(null);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [deleteConfirmItem, setDeleteConfirmItem] = useState(null);
    const contentRef = useRef([]);

    /** Request one streamed ZIP instead of loading every attachment separately. */
    const onDownloadClicked = async (downloadOption) => {
        const zipFilename = content[0]?.parent?.slug
            ? `${parent?.slug}.zip`
            : `${title}.zip`;
        const body = JSON.stringify({
            download_option: downloadOption,
            parent: parent?.id || null,
            selected,
            archive_name: zipFilename,
        });

        try {
            setProgress({ show: true, value: null });
            const response = await post(
                dispatch,
                "/api/content/attachments",
                body,
                {
                    responseType: "blob",
                    onDownloadProgress: ({ loaded, total }) => {
                        setProgress({
                            show: true,
                            value: total ? (loaded / total) * 100 : null,
                        });
                    },
                }
            );
            saveAs(response.data, zipFilename);
            dispatch(
                showAlert({
                    message: t(
                        "ContentList.downloadAttachments.zippingFinished"
                    ),
                    severity: "success",
                })
            );
        } catch (error) {
            console.error(error);
            dispatch(
                showAlert({
                    message: t("ContentList.downloadAttachments.error"),
                    severity: "error",
                })
            );
        } finally {
            setProgress({ show: false, value: 0 });
        }
    };

    /** Update the News visibility flag for every currently selected item. */
    const updateSelectedNewsVisibility = async (news) => {
        try {
            setLoading(true);
            await post(
                dispatch,
                "/api/content/admin/news/",
                JSON.stringify({ selected, news })
            );
            setSelected([]);
            dispatch(
                showAlert({
                    message: t(
                        news
                            ? "ContentList.newsVisibility.showSuccess"
                            : "ContentList.newsVisibility.hideSuccess"
                    ),
                    severity: "success",
                })
            );
            await visitPage();
        } catch (err) {
            dispatch(
                showAlert({
                    message:
                        err.response?.data?.error ||
                        t("ContentList.newsVisibility.error"),
                    severity: "error",
                })
            );
        } finally {
            setLoading(false);
        }
    };

    const options = [
        ...(isAdmin
            ? [
                  {
                      name: "showInNews",
                      label: t(
                          "AdminTableCommons.contextMenuOptions.showInNews"
                      ),
                      handler: () => updateSelectedNewsVisibility(true),
                  },
                  {
                      name: "hideFromNews",
                      label: t(
                          "AdminTableCommons.contextMenuOptions.hideFromNews"
                      ),
                      handler: () => updateSelectedNewsVisibility(false),
                  },
              ]
            : []),
        {
            name: "selectAllCurrentPage",
            label: t("ContentList.selectAllCurrentPage"),
            handler: handleSelectAllCurrentPage,
        },
        ...(parent
            ? [
                  {
                      name: "all",
                      label: t(
                          "AdminTableCommons.contextMenuOptions.downloadAll"
                      ),
                      handler: onDownloadClicked,
                  },
              ]
            : []),
        {
            name: "selected",
            label: t("AdminTableCommons.contextMenuOptions.downloadSelected"),
            handler: onDownloadClicked,
        },
        {
            name: "clear",
            label: t("AdminTableCommons.contextMenuOptions.clearSelection"),
            handler: () => {
                setSelected([]);
            },
        },
    ];

    const handleSelect = (slug) => {
        setSelected((prev) =>
            prev.includes(slug)
                ? prev.filter((s) => s !== slug)
                : [...prev, slug]
        );
    };

    /** Replace the selection with every content card displayed on this page. */
    function handleSelectAllCurrentPage() {
        setSelected(content.map((item) => item.content.slug));
    }
    const handleContextMenu = (event, item) => {
        if (!isAdmin) {
            return;
        }
        event.preventDefault();
        setContextItem(item);
        contextItemRef.current = item;
        setContextMenu(
            contextMenu === null
                ? {
                      mouseX: event.clientX - 2,
                      mouseY: event.clientY - 4,
                  }
                : null
        );
    };

    const handleCloseContextMenu = () => {
        setContextMenu(null);
    };
    const deleteContent = async (item) => {
        if (!item?.content?.id) {
            dispatch(
                showAlert({
                    message: t("AdminContent.update.error"),
                    severity: "error",
                })
            );
            return;
        }
        try {
            await post(
                dispatch,
                "/api/content/admin/delete/",
                JSON.stringify({
                    ids: [item.content.id],
                })
            );
            setContent((prev) =>
                prev.filter((c) => c.content.id !== item.content.id)
            );
            setCount((prev) => (prev > 0 ? prev - 1 : 0));
            dispatch(
                showAlert({
                    message: t("buttonCommons.delete"),
                    severity: "success",
                })
            );
        } catch (err) {
            const status = err.response?.status;
            const data = err.response?.data;
            const message =
                (err.response &&
                    err.response.data &&
                    (err.response.data.error || err.response.data.detail)) ||
                (data ? JSON.stringify(data) : null) ||
                (status ? `HTTP ${status}` : null) ||
                err.message ||
                t("AdminContent.update.error");
            dispatch(
                showAlert({
                    message,
                    severity: "error",
                })
            );
        }
    };
    const openEditDialog = (item) => {
        if (!item?.content?.slug || !item?.content?.type) {
            return;
        }
        setDialogSlug(item.content.slug);
        setDialogContentType(item.content.type);
        setShowModal(true);
    };
    const handleDeleteMenuClick = async (event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        const item = contextItemRef.current || contextItem;
        handleCloseContextMenu();
        setContextItem(null);
        contextItemRef.current = null;
        setDeleteConfirmItem(item || null);
    };

    const handleDeleteConfirmClose = () => {
        setDeleteConfirmItem(null);
    };

    const handleDeleteConfirmSubmit = async () => {
        const item = deleteConfirmItem;
        setDeleteConfirmItem(null);
        await deleteContent(item);
    };

    const handleChange = (event, value) => {
        setPage(value);
        updateHistory(value);
    };

    const handlePageCountChanged = (newVal) => {
        setPageCount(newVal);
        if (page !== 1) {
            setPage(1);
            updateHistory(1);
        }
    };

    const updateHistory = (pageVal) => {
        const tabQuery = tab ? `tab=${tab}&` : "";
        window.history.pushState(
            "",
            "",
            `${location.pathname}?${tabQuery}page=${pageVal}`
        );
    };

    const visitPage = async () => {
        window.scrollTo(0, 0);

        const pageCountValue =
            pageCount === "all" ? (count && count > 0 ? count : 1000) : pageCount;
        const body = JSON.stringify({
            page_count: pageCountValue,
            sort_by: "latest",
        });

        try {
            setLoading(true);
            const res = await post(
                dispatch,
                `${endpoint}${slug}?page=${page}`,
                body
            );
            setContent(res.data.results);
            setParent(res.data.results[0]?.parent || null);
            setCount(res.data.count);
            if (
                label == null &&
                res.data.results[0].parent &&
                res.data.results[0].parent.title
            ) {
                setTitle(res.data.results[0].parent.title);
            }
        } catch (err) {
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const setInitialPage = () => {
            const pageParam = searchParams.get("page");
            if (
                pageParam !== null &&
                pageParam !== undefined &&
                !isNaN(pageParam) &&
                Number(pageParam) !== page
            ) {
                setPage(Number(pageParam));
            } else {
                setPage(1);
            }
        };

        setInitialPage();
    }, []);

    useEffect(() => {
        if (page !== undefined && page !== null && page >= 0) {
            visitPage();
        }
    }, [page, pageCount, slug]);
    useEffect(() => {
        contentRef.current = content;
    }, [content]);

    const hasNavIds = useMemo(
        () => content.every((item) => item && item.nav_id),
        [content]
    );
    const isReorderable =
        isAdmin && parent && Number(page) >= 1 && hasNavIds;
    const contentPerPageOptions = [
        ...DEFAULT_CONTENT_PER_PAGE,
        { value: "all", label: t("ContentList.showAll") },
    ];
    const selectedPageOption =
        contentPerPageOptions.find((option) => option.value === pageCount) ||
        contentPerPageOptions[1];

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
    );
    const isSameId = useCallback((left, right) => {
        if (left == null || right == null) {
            return false;
        }
        return String(left) === String(right);
    }, []);

    const handleDndStart = useCallback(({ active }) => {
        if (!isReorderable) {
            return;
        }
        dndMovedRef.current = false;
        setSuppressNavigationClick(false);
        setActiveDrag(true);
        setActiveId(active.id);
        initialOrderRef.current = new Map(
            contentRef.current.map((c, idx) => [c.nav_id, idx])
        );
    }, [isReorderable]);
    const handleDndOver = useCallback(({ active, over }) => {
        if (!isReorderable || !over || isSameId(active.id, over.id)) {
            return;
        }
        setContent((items) => {
            const oldIndex = items.findIndex((c) => isSameId(c.nav_id, active.id));
            const newIndex = items.findIndex((c) => isSameId(c.nav_id, over.id));
            if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
                return items;
            }
            dndMovedRef.current = true;
            return arrayMove(items, oldIndex, newIndex);
        });
    }, [isReorderable, isSameId]);
    const handleDndEnd = useCallback(async ({ active, over }) => {
        setActiveId(null);
        setActiveDrag(false);
        if (dndMovedRef.current) {
            setSuppressNavigationClick(true);
        }
        dndMovedRef.current = false;
        if (!isReorderable || !over) {
            return;
        }
        const pageSize =
            pageCount === "all" ? count : Number(pageCount);
        const offset = Math.max(0, (Number(page) - 1) * pageSize);
        const oldOrder = initialOrderRef.current;
        const latestItems = contentRef.current;
        const modified = latestItems
            .map((c, idx) => ({
                id: c.nav_id,
                parent: c.nav_parent_id,
                position: offset + idx,
                title: c.content?.title,
                type: c.content?.type,
                changed: true,
                _old: oldOrder.get(c.nav_id),
            }))
            .filter((c) => c._old !== c.position)
            .map(({ _old, ...c }) => c);

        if (!modified.length) {
            return;
        }

        const payload = {
            data: {
                userId: user ? user.id : null,
                id: -1,
                type: "navigation",
                status: false,
                title: "",
                body: null,
                sticky: 0,
                news: true,
                groups: [],
            },
            nav: {
                modified,
                removed: [],
            },
        };

        try {
            const res = await post(dispatch, `/api/content/admin/add/`, payload);
            dispatch(
                showAlert({
                    message: t("ContentList.reorder.success"),
                    severity: "success",
                })
            );
        } catch (err) {
            dispatch(
                showAlert({
                    message:
                        (err.response &&
                            err.response.data &&
                            (err.response.data.nav || err.response.data.error)) ||
                        err.message ||
                        t("ContentList.reorder.error"),
                    severity: "error",
                })
            );
            visitPage();
        }
    }, [count, dispatch, isReorderable, page, pageCount, t, visitPage]);
    const handleDndCancel = useCallback(() => {
        setActiveId(null);
        setActiveDrag(false);
        if (dndMovedRef.current) {
            setSuppressNavigationClick(true);
        }
        dndMovedRef.current = false;
    }, []);

    const renderContents = () => {
        if (loading) {
            return <Loader />;
        }
        if (content && content.length > 0) {
            return (
                <>
                    {selected.length > 0 && (
                        <SelectionBar numSelected={selected.length}>
                            {progress && progress.show && (
                                <Box sx={{ width: "100%" }}>
                                    <LinearProgress
                                        variant={
                                            Number.isFinite(progress.value)
                                                ? "determinate"
                                                : "indeterminate"
                                        }
                                        value={progress.value || 0}
                                    />
                                </Box>
                            )}
                            <DownloadButton options={options} />
                        </SelectionBar>
                    )}
                    {content[0].parent && content[0].parent.body && (
                        <div
                            className="mt-5 mb-5"
                            dangerouslySetInnerHTML={{
                                __html: content[0].parent.body,
                            }}
                        />
                    )}
                    <Stack
                        direction="row"
                        flexWrap="wrap-reverse"
                        alignItems="center"
                        justifyContent="flex-end"
                        sx={{ mb: 5 }}
                    >
                        <Stack flexShrink={0} sx={{ my: 1 }}>
                            <ContentSortMenu
                                title={t("ContentList.contentPerPage")}
                                initialValue={selectedPageOption}
                                options={contentPerPageOptions}
                                onSort={handlePageCountChanged}
                                key={selectedPageOption.value}
                            />
                        </Stack>
                    </Stack>
                    {content.length > 0 && (
                        <DndContext
                            sensors={sensors}
                            collisionDetection={pointerWithin}
                            onDragStart={handleDndStart}
                            onDragOver={handleDndOver}
                            onDragEnd={handleDndEnd}
                            onDragCancel={handleDndCancel}
                        >
                            <SortableContext
                                items={content
                                    .filter((c) => c && c.nav_id)
                                    .map((c) => c.nav_id)}
                                strategy={rectSortingStrategy}
                            >
                                <ProductList
                                    products={content}
                                    selected={selected}
                                    onSelect={handleSelect}
                                    draggable={isReorderable}
                                    activeId={activeId}
                                    activeDrag={activeDrag}
                                    suppressNavigationClick={
                                        suppressNavigationClick ||
                                        activeDrag ||
                                        Boolean(activeId)
                                    }
                                    onConsumeNavigationSuppression={() => {
                                        setSuppressNavigationClick(false);
                                    }}
                                    onContextMenu={handleContextMenu}
                                />
                            </SortableContext>
                        <DragOverlay>
                            {activeId ? (
                                (() => {
                                    const activeItem = content.find(
                                        (c) => c && isSameId(c.nav_id, activeId)
                                    );
                                    const thumb =
                                        activeItem?.thumbnail?.filePath ||
                                        activeItem?.thumbnail?.file_path ||
                                        null;
                                    return (
                                        <Card sx={{ width: 260 }}>
                                            <Box
                                                sx={{
                                                    height: 140,
                                                    p: 1,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    bgcolor: "grey.100",
                                                }}
                                            >
                                                {thumb && (
                                                    <Box
                                                        component="img"
                                                        src={thumb}
                                                        alt={activeItem?.content?.title || ""}
                                                        sx={{
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit: "contain",
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            <CardContent>
                                                <Typography variant="subtitle2">
                                                    {activeItem?.content?.title || ""}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    );
                                })()
                            ) : null}
                        </DragOverlay>
                        </DndContext>
                    )}{" "}
                    {isAdmin && (
                        <Menu
                            open={contextMenu !== null}
                            onClose={handleCloseContextMenu}
                            onContextMenu={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                            }}
                            anchorReference="anchorPosition"
                            anchorPosition={
                                contextMenu !== null
                                    ? {
                                          top: contextMenu.mouseY,
                                          left: contextMenu.mouseX,
                                      }
                                    : undefined
                            }
                        >
                            <MenuItem
                                onClick={() => {
                                    const item = contextItemRef.current || contextItem;
                                    openEditDialog(item);
                                    handleCloseContextMenu();
                                    setContextItem(null);
                                    contextItemRef.current = null;
                                }}
                            >
                                {t("buttonCommons.edit")}
                            </MenuItem>
                            <MenuItem
                                onClick={handleDeleteMenuClick}
                            >
                                {t("buttonCommons.delete")}
                            </MenuItem>
                        </Menu>
                    )}
                    <Box
                        sx={{
                            mt: 5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Pagination
                            count={
                                pageCount === "all"
                                    ? 1
                                    : Math.ceil(count / pageCount)
                            }
                            page={page}
                            onChange={handleChange}
                        />
                    </Box>{" "}
                </>
            );
        } else {
            return <Page404 />;
        }
    };

    return (
        <Page title={`Extranet: ${title}`}>
            <Container maxWidth={false}>
                <Typography variant="h4" sx={{ mb: 5 }}>
                    {title}
                </Typography>
                {isAdmin && (
                    <ContentDialog
                        open={showModal && dialogContentType !== null}
                        onClose={() => {
                            setShowModal(false);
                        }}
                        type="edit"
                        contentType={dialogContentType}
                        slug={dialogSlug}
                        submitStatus={setSubmitStatus}
                    />
                )}
                <ConfirmDialog
                    open={Boolean(deleteConfirmItem)}
                    onClose={handleDeleteConfirmClose}
                    onSubmit={handleDeleteConfirmSubmit}
                    title={t("ConfirmDialog.deleteTitle", {
                        defaultValue: "Confirm deletion",
                    })}
                    text={t("ConfirmDialog.deleteContentText", {
                        defaultValue:
                            "Do you really want to delete this content? This action cannot be undone.",
                    })}
                    confirmLabel={t("buttonCommons.delete")}
                    confirmColor="error"
                />
                {renderContents()}
            </Container>
        </Page>
    );
}
